import api, { ApiResponse, PaginatedApiResponse } from './axiosInstance';

// User interface for populated fields
export interface User {
  _id: string;
  name: string;
  email?: string;
}

// UoM Master interface matching backend
export interface UoMMaster {
  _id: string;
  name: string;
  symbol: string;
  category: string;
  description?: string;
  isActive: boolean;
  createdBy: string | User;
  updatedBy: string | User;
  createdAt: string;
  updatedAt: string;
}

// Create UoM Master data interface
export interface CreateUoMMasterData {
  name: string;
  symbol: string;
  category: string;
  description?: string;
}

// Update UoM Master data interface
export interface UpdateUoMMasterData extends Partial<CreateUoMMasterData> {}

// Query parameters interface
export interface UoMMasterQueryParams {
  id?: string; // For getting single record
  page?: number;
  limit?: number;
  search?: string;
  name?: string;
  symbol?: string;
  category?: string;
  isActive?: boolean;
  createdBy?: string;
  sortBy?: string;
  projectBy?: string;
}

class UoMMasterService {
  private baseUrl = '/uom-masters';

  private normalizeUoMMaster = (raw: any): UoMMaster => {
    // Handle populated user objects - keep the full object if populated, otherwise use ID string
    let createdBy: string | User = '';
    if (raw?.createdBy) {
      if (typeof raw.createdBy === 'object' && raw.createdBy !== null) {
        // Check if it's a populated user object (has name property)
        if (raw.createdBy.name) {
          createdBy = {
            _id: raw.createdBy._id || raw.createdBy.id || '',
            name: raw.createdBy.name,
            email: raw.createdBy.email
          };
        } else {
          // It's an ObjectId object, extract the ID
          createdBy = raw.createdBy._id || raw.createdBy.id || raw.createdBy.toString() || '';
        }
      } else {
        // It's a string ID
        createdBy = raw.createdBy;
      }
    }

    let updatedBy: string | User = '';
    if (raw?.updatedBy) {
      if (typeof raw.updatedBy === 'object' && raw.updatedBy !== null) {
        // Check if it's a populated user object (has name property)
        if (raw.updatedBy.name) {
          updatedBy = {
            _id: raw.updatedBy._id || raw.updatedBy.id || '',
            name: raw.updatedBy.name,
            email: raw.updatedBy.email
          };
        } else {
          // It's an ObjectId object, extract the ID
          updatedBy = raw.updatedBy._id || raw.updatedBy.id || raw.updatedBy.toString() || '';
        }
      } else {
        // It's a string ID
        updatedBy = raw.updatedBy;
      }
    }

    return {
      _id: raw?._id ?? raw?.id ?? '',
      name: raw?.name ?? '',
      symbol: raw?.symbol ?? '',
      category: raw?.category ?? '',
      description: raw?.description,
      isActive: raw?.isActive ?? true,
      createdBy,
      updatedBy,
      createdAt: raw?.createdAt ?? '',
      updatedAt: raw?.updatedAt ?? '',
    } as UoMMaster;
  };

  /**
   * Get all UoM Masters with pagination and filtering, or get by ID
   */
  async getUoMMasters(params?: UoMMasterQueryParams): Promise<PaginatedApiResponse<UoMMaster> | ApiResponse<UoMMaster>> {
    try {
      const response = await api.get<PaginatedApiResponse<any> | ApiResponse<any>>(this.baseUrl, { params });
      
      // If id is provided, return single record response
      if (params?.id) {
        const normalized: ApiResponse<UoMMaster> = {
          ...response.data,
          data: this.normalizeUoMMaster(response.data?.data),
        };
        return normalized;
      }

      // Check if response has meta (paginated) or just data (non-paginated)
      if ('meta' in response.data) {
        // Paginated response
        const normalized: PaginatedApiResponse<UoMMaster> = {
          ...response.data,
          data: (response.data?.data ?? []).map((u: any) => this.normalizeUoMMaster(u)),
        };
        return normalized;
      } else {
        // Non-paginated response
        return {
          success: response.data.success,
          message: response.data.message,
          data: (response.data?.data ?? []).map((u: any) => this.normalizeUoMMaster(u)),
        };
      }
    } catch (error: any) {
      console.error('Error fetching UoM Masters:', error);
      throw new Error(error.message || 'Failed to fetch UoM Masters');
    }
  }

  /**
   * Get a single UoM Master by ID
   */
  async getUoMMasterById(id: string): Promise<ApiResponse<UoMMaster>> {
    try {
      const response = await api.get<ApiResponse<any>>(`${this.baseUrl}?id=${id}`);
      const normalized: ApiResponse<UoMMaster> = {
        ...response.data,
        data: this.normalizeUoMMaster(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error fetching UoM Master:', error);
      throw new Error(error.message || 'Failed to fetch UoM Master');
    }
  }

  /**
   * Create a new UoM Master
   */
  async createUoMMaster(uomMasterData: CreateUoMMasterData): Promise<ApiResponse<UoMMaster>> {
    try {
      const response = await api.post<ApiResponse<any>>(this.baseUrl, uomMasterData);
      const normalized: ApiResponse<UoMMaster> = {
        ...response.data,
        data: this.normalizeUoMMaster(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error creating UoM Master:', error);
      throw new Error(error.message || 'Failed to create UoM Master');
    }
  }

  /**
   * Update a UoM Master
   */
  async updateUoMMaster(id: string, uomMasterData: UpdateUoMMasterData): Promise<ApiResponse<UoMMaster>> {
    try {
      const response = await api.put<ApiResponse<any>>(`${this.baseUrl}/${id}`, uomMasterData);
      const normalized: ApiResponse<UoMMaster> = {
        ...response.data,
        data: this.normalizeUoMMaster(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error updating UoM Master:', error);
      throw new Error(error.message || 'Failed to update UoM Master');
    }
  }

  /**
   * Activate a UoM Master
   */
  async activateUoMMaster(id: string): Promise<ApiResponse<UoMMaster>> {
    try {
      const response = await api.post<ApiResponse<any>>(`${this.baseUrl}/${id}/activate`);
      const normalized: ApiResponse<UoMMaster> = {
        ...response.data,
        data: this.normalizeUoMMaster(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error activating UoM Master:', error);
      throw new Error(error.message || 'Failed to activate UoM Master');
    }
  }

  /**
   * Inactivate a UoM Master
   */
  async inactivateUoMMaster(id: string): Promise<ApiResponse<UoMMaster>> {
    try {
      const response = await api.post<ApiResponse<any>>(`${this.baseUrl}/${id}/inactivate`);
      const normalized: ApiResponse<UoMMaster> = {
        ...response.data,
        data: this.normalizeUoMMaster(response.data?.data),
      };
      return normalized;
    } catch (error: any) {
      console.error('Error inactivating UoM Master:', error);
      throw new Error(error.message || 'Failed to inactivate UoM Master');
    }
  }

  /**
   * Get all unique categories
   */
  async getCategories(): Promise<string[]> {
    try {
      const response = await api.get<PaginatedApiResponse<any>>(this.baseUrl, {
        params: { limit: 1000, isActive: true }
      });
      
      const uoms = response.data?.data ?? [];
      const categories = [...new Set(uoms.map((u: any) => u.category))].filter(Boolean);
      return categories.sort();
    } catch (error: any) {
      console.error('Error fetching UOM categories:', error);
      throw new Error(error.message || 'Failed to fetch UOM categories');
    }
  }

  /**
   * Get UOMs by category
   */
  async getUomsByCategory(category: string): Promise<UoMMaster[]> {
    try {
      const response = await api.get<PaginatedApiResponse<any>>(this.baseUrl, {
        params: { category, limit: 1000, isActive: true }
      });
      
      return (response.data?.data ?? []).map((u: any) => this.normalizeUoMMaster(u));
    } catch (error: any) {
      console.error('Error fetching UOMs by category:', error);
      throw new Error(error.message || 'Failed to fetch UOMs by category');
    }
  }

  /**
   * Get dropdown options for a category (returns {value, label} format)
   */
  async getUomDropdownOptions(category: string): Promise<Array<{ value: string; label: string }>> {
    try {
      const uoms = await this.getUomsByCategory(category);
      return uoms.map(uom => ({
        value: uom.symbol,
        label: uom.symbol  // Show only symbol
      }));
    } catch (error: any) {
      console.error('Error fetching UOM dropdown options:', error);
      throw new Error(error.message || 'Failed to fetch UOM dropdown options');
    }
  }
}

export const uomMasterService = new UoMMasterService();
export default uomMasterService;
